import React, { useCallback, useContext, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createErrorBanner, createInfoBanner } from 'components/Banner';
import { Dropdown, DropdownMenu, DropdownItem } from 'components/Dropdown2';
import Empty from 'components/Empty';
import FeatureNotSupported from 'components/FeatureNotSupported';
import { FlyoutContext, FlyoutProvider } from 'components/Flyout/FlyoutContext';
import Message from 'components/Message';
import { openModal, closeModal } from 'components/Modal';
import { createErrorSnackbar } from 'components/Snackbar';
import { Table } from 'components/Table';
import Tabs, { Tab, TabLabel } from 'components/Tabs';
import { ICONS, NAV_ICONS } from 'constants/icons';
import { Title } from 'features/App';
import { bem } from 'helpers/class-name';
import { IA_KEY } from 'helpers/ia-logic';
import { formatEmailSubjectDateTime } from 'helpers/intl-fns';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenue, selectIaLogic } from 'selectors/general';
import { selectIsDesktop } from 'selectors/ui';

import SkuOrderFlyout, { SKU_ORDER_FLYOUT_ID } from '../shared/SkuOrderFlyout';
import SkuVenueOpenStatus from '../shared/SkuVenueOpenStatus';

import { buildCancelMessageHtml, getOrderItemNames, mapOrderToTableRow } from './SkuOrderDashboard.helpers';
import messages from './SkuOrderDashboard.messages';
import './SkuOrderDashboard.scss';
import OrderStatusCell from './SkuOrderStatusCell';
import { OrderTableHeader, MobileOrderTableHeader, CellValue } from './SkuOrderTable';
import { ORDER_ITEM_CANCELLATION_TYPE, PARTNER_ORDERS_TAB } from './SkuOrders.constants';
import {
  useApplyOptimisticOrderCancelled,
  useCancelSkuOrder,
  useFulfillSkuOrder,
  useInvalidatePartnerOrders,
  usePartnerOrders,
} from './SkuOrders.hooks';

const { block, element } = bem('sku-orders-dashboard');

const ItemsCell = ({ groups }) => {
  if (!groups?.length) return <CellValue value={null} />;

  return (
    <span className={element('items-cell')}>
      {groups.map((group, idx) => (
        <React.Fragment key={group.itemId}>
          {idx > 0 && ', '}
          <span className={element('items-cell-name', { 'crossed-out': group.isCrossedOut })}>
            {group.count > 1 ? `${group.name} (x${group.count})` : group.name}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
};

ItemsCell.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      isCrossedOut: PropTypes.bool.isRequired,
    })
  ),
};

const TAB_TODAY = PARTNER_ORDERS_TAB.TODAY;
const TAB_HISTORY = PARTNER_ORDERS_TAB.HISTORY;

const SkuOrderDashboardContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { venueId, timezone: venueTimezone, venueAliasUrl, venueName } = useSelector(selectActiveVenue);
  const { featureIsSupported } = useSelector(state => selectIaLogic(state, IA_KEY.SKU_ORDERS, venueId));
  const isDesktop = useSelector(selectIsDesktop);
  const [activeTab, setActiveTab] = useState(TAB_TODAY);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [fulfillingOrderId, setFulfillingOrderId] = useState(null);

  const { openFlyout } = useContext(FlyoutContext);
  const invalidatePartnerOrders = useInvalidatePartnerOrders();
  const applyOptimisticOrderCancelled = useApplyOptimisticOrderCancelled();

  const {
    data: ordersData,
    isLoading,
    isError,
  } = usePartnerOrders(activeTab, {
    onError: () => {
      dispatch(createErrorBanner(intl.formatMessage(messages.loadOrdersError)));
    },
  });

  const orders = ordersData?.orders ?? [];

  const { mutate: cancelOrder } = useCancelSkuOrder();

  const { mutate: fulfillOrder } = useFulfillSkuOrder({
    onSuccess: () => {
      invalidatePartnerOrders();
      setFulfillingOrderId(null);
    },
    onError: () => {
      dispatch(createErrorBanner(intl.formatMessage(messages.fulfillOrderError)));
      setFulfillingOrderId(null);
    },
  });

  const handleFulfill = useCallback(
    orderId => {
      setFulfillingOrderId(orderId);
      fulfillOrder(orderId);
    },
    [fulfillOrder]
  );

  const openCancelModal = useCallback(
    (orderId, cancellationType) => {
      const order = orders.find(o => o?.core?.orderId === orderId) || null;
      if (!order) return;

      const orderCreatedOn = order?.core?.createdOn;
      const activeVenueName = venueName || '';
      const userName = order?.userName || '';
      const itemName = getOrderItemNames(order?.orderItems);
      const isOutOfStock = cancellationType === ORDER_ITEM_CANCELLATION_TYPE.PARTNER_SOLD_OUT;

      const subjectValues = orderCreatedOn
        ? { ...formatEmailSubjectDateTime(orderCreatedOn, venueTimezone, intl), venueName: activeVenueName }
        : { time: '', date: '', venueName: activeVenueName };

      const subject = intl.formatMessage(
        isOutOfStock ? messages.cancelOrderSubjectOutOfStock : messages.cancelOrderSubjectPartnerCancelled,
        { venueName: activeVenueName }
      );
      const messageHtml = buildCancelMessageHtml(intl, isOutOfStock, {
        time: subjectValues.time,
        date: subjectValues.date,
        venueName: activeVenueName,
        itemName,
        venueAliasUrl,
      });

      dispatch(
        openModal('SkuCancelConfirmModal', {
          onSubmit: (_values, { resolveSubmit, rejectSubmit }) => {
            cancelOrder(orderId, {
              onSuccess: () => {
                resolveSubmit();
                dispatch(closeModal());
                applyOptimisticOrderCancelled(orderId, cancellationType);
                dispatch(createInfoBanner(messages.cancelOrderSuccess));
              },
              onError: () => {
                rejectSubmit();
                dispatch(closeModal());
                dispatch(createErrorSnackbar(isOutOfStock ? messages.itemOutOfStockError : messages.cancelOrderError));
              },
            });
          },
          title: <Message message={messages.cancelOrderModalTitle} />,
          ...(isOutOfStock ? {} : { acceptMessage: messages.cancelOrderNotifyButton }),
          to: userName,
          subject,
          initialValues: { message: messageHtml },
        })
      );
    },
    [dispatch, intl, venueTimezone, venueAliasUrl, venueName, orders, cancelOrder, applyOptimisticOrderCancelled]
  );

  const handleCancelOrder = useCallback(
    orderId => openCancelModal(orderId, ORDER_ITEM_CANCELLATION_TYPE.PARTNER_CANCELLED),
    [openCancelModal]
  );

  const handleItemOutOfStock = useCallback(
    orderId => openCancelModal(orderId, ORDER_ITEM_CANCELLATION_TYPE.PARTNER_SOLD_OUT),
    [openCancelModal]
  );

  const ordersForTab = useMemo(
    () =>
      orders.map(order =>
        mapOrderToTableRow(order, {
          intl,
          venueTimezone,
          locale: intl.locale,
        })
      ),
    [orders, intl, venueTimezone]
  );

  const handleRowClick = useCallback(
    orderId => {
      setSelectedOrderId(orderId);
      openFlyout(SKU_ORDER_FLYOUT_ID);
    },
    [openFlyout]
  );

  const selectedOrder = useMemo(
    () => orders.find(o => o?.core?.orderId === selectedOrderId) || null,
    [orders, selectedOrderId]
  );

  if (!featureIsSupported) {
    return (
      <>
        <Message message={messages.pageTitle} as={Title} />
        <FeatureNotSupported />
      </>
    );
  }

  const showEmpty = !isLoading && !isError && ordersForTab.length === 0;

  return (
    <>
      <Message message={messages.pageTitle} as={Title} />

      <div className={block()}>
        <Tabs selectedTab={activeTab} onTabChange={setActiveTab}>
          <Tab name={TAB_TODAY}>
            <TabLabel>
              <Message message={messages.tabToday} />
            </TabLabel>
            <div className={element('tab-controls')}>
              <SkuVenueOpenStatus />
              <Dropdown
                attachment="right"
                className={element('settings-dropdown-wrapper')}
                contentClassName={element('settings-dropdown-content')}>
                {({ toggleMenu, closeMenu }) => (
                  <>
                    <button
                      type="button"
                      className={element('settings-btn')}
                      aria-label={intl.formatMessage(messages.settingsButton)}
                      onClick={toggleMenu}>
                      <Isvg src={ICONS.cog} className={element('cog-icon')} />
                    </button>
                    <DropdownMenu className={element('settings-dropdown')}>
                      <DropdownItem
                        onClick={() => {
                          Segment.event('Click Manage Items', { venueId, type: 'venue' });
                          navigate(makePath.items(venueId));
                          closeMenu();
                        }}>
                        <span className={element('settings-menu-item')}>
                          <Isvg src={ICONS.editPencilSku} className={element('settings-menu-icon')} />
                          <Message message={messages.manageItems} />
                        </span>
                      </DropdownItem>
                    </DropdownMenu>
                  </>
                )}
              </Dropdown>
            </div>
          </Tab>
          <Tab name={TAB_HISTORY}>
            <TabLabel>
              <Message message={messages.tabHistory} />
            </TabLabel>
          </Tab>
        </Tabs>

        {showEmpty ? (
          <Empty
            icon={NAV_ICONS.appointments}
            title={messages.emptyOrders}
            subtitle={{ ...messages.emptyOrdersSubtitle, values: { br: <br /> } }}
          />
        ) : (
          <Table
            header={isDesktop ? <OrderTableHeader /> : <MobileOrderTableHeader />}
            emptyMessage={messages.emptyOrders}
            isLoading={isLoading}>
            {ordersForTab.map(order =>
              isDesktop ? (
                <tr
                  key={order.id}
                  className={element('order-row')}
                  onClick={() => handleRowClick(order.rawOrderId)}
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') handleRowClick(order.rawOrderId);
                  }}>
                  <td>
                    <CellValue value={order.memberName} />
                  </td>
                  <td>
                    <CellValue value={order.id} />
                  </td>
                  <td>
                    <ItemsCell groups={order.itemGroups} />
                  </td>
                  <td>
                    <CellValue value={order.payableAmountDisplay} align="right" />
                  </td>
                  <td>
                    <CellValue value={order.orderPlaced} align="right" />
                  </td>
                  <td>
                    <CellValue value={order.pickupTime} align="right" />
                  </td>
                  <td>
                    <OrderStatusCell
                      lifecycleStatus={order.lifecycleStatus}
                      showRowActions={isDesktop}
                      onFulfill={() => handleFulfill(order.rawOrderId)}
                      isFulfilling={fulfillingOrderId === order.rawOrderId}
                      onEditItem={() => handleRowClick(order.rawOrderId)}
                      onCancelOrder={() => handleCancelOrder(order.rawOrderId)}
                      onItemOutOfStock={() => handleItemOutOfStock(order.rawOrderId)}
                      userName={order.memberName}
                      userId={order.userId}
                      venueName={venueName}
                      orderCreatedOn={order.createdOn}
                    />
                  </td>
                </tr>
              ) : (
                <tr
                  key={order.id}
                  className={element('order-row')}
                  onClick={() => handleRowClick(order.rawOrderId)}
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') handleRowClick(order.rawOrderId);
                  }}>
                  <td>
                    <CellValue value={order.id} emphasis />
                  </td>
                  <td>
                    <OrderStatusCell
                      lifecycleStatus={order.lifecycleStatus}
                      showRowActions={isDesktop}
                      onFulfill={() => handleFulfill(order.rawOrderId)}
                      isFulfilling={fulfillingOrderId === order.rawOrderId}
                      onEditItem={() => handleRowClick(order.rawOrderId)}
                      onCancelOrder={() => handleCancelOrder(order.rawOrderId)}
                      onItemOutOfStock={() => handleItemOutOfStock(order.rawOrderId)}
                      userName={order.memberName}
                      userId={order.userId}
                      venueName={venueName}
                      orderCreatedOn={order.createdOn}
                    />
                  </td>
                </tr>
              )
            )}
          </Table>
        )}
      </div>

      <SkuOrderFlyout
        order={selectedOrder ?? undefined}
        onFulfill={selectedOrder?.core?.orderId ? () => handleFulfill(selectedOrder.core.orderId) : undefined}
        isFulfilling={fulfillingOrderId === selectedOrder?.core?.orderId}
        onCancelOrder={selectedOrder?.core?.orderId ? () => handleCancelOrder(selectedOrder.core.orderId) : undefined}
        onItemOutOfStock={
          selectedOrder?.core?.orderId ? () => handleItemOutOfStock(selectedOrder.core.orderId) : undefined
        }
      />
    </>
  );
};

const SkuOrderDashboard = () => (
  <FlyoutProvider>
    <SkuOrderDashboardContent />
  </FlyoutProvider>
);

export default SkuOrderDashboard;
